export interface UnavailableRange {
  checkIn: string;
  checkOut: string;
}

function calendarDate(value: string): string {
  if (!/^\d{8}$/.test(value)) throw new Error('Expected an all-day calendar date');
  const date = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  const parsed = new Date(`${date}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) {
    throw new Error('Invalid calendar date');
  }
  return date;
}

/** Airbnb exports all-day events; DTEND is the exclusive checkout date. */
export function parseAvailabilityCalendar(text: string): UnavailableRange[] {
  const lines = text.replace(/\r\n?/g, '\n').replace(/\n[ \t]/g, '').trim().split('\n');
  if (lines[0] !== 'BEGIN:VCALENDAR' || lines.at(-1) !== 'END:VCALENDAR') {
    throw new Error('Incomplete calendar');
  }
  const dates: UnavailableRange[] = [];
  let event: Record<string, string> | null = null;
  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') {
      if (event) throw new Error('Incomplete event');
      event = {};
    } else if (line === 'END:VEVENT') {
      if (!event) throw new Error('Unexpected event ending');
      if (event.STATUS !== 'CANCELLED') {
        if (event.RRULE || event.RDATE || event.EXDATE || event['RECURRENCE-ID']) {
          throw new Error('Recurring events are not supported by this Airbnb mirror');
        }
        const checkIn = calendarDate(event.DTSTART || '');
        const checkOut = calendarDate(event.DTEND || '');
        if (checkOut <= checkIn) throw new Error('Invalid calendar range');
        dates.push({ checkIn, checkOut });
      }
      event = null;
    } else if (event) {
      const colon = line.indexOf(':');
      if (colon < 0) continue;
      const key = line.slice(0, colon).split(';')[0];
      // Guest names, reservation references and descriptions are never returned.
      if (['DTSTART', 'DTEND', 'STATUS', 'RRULE', 'RDATE', 'EXDATE', 'RECURRENCE-ID'].includes(key)) {
        if (key in event) throw new Error('Duplicate calendar field');
        event[key] = line.slice(colon + 1);
      }
    }
  }
  if (event) throw new Error('Incomplete event');
  return [...new Map(dates.map(range => [`${range.checkIn}/${range.checkOut}`, range])).values()]
    .sort((a, b) => a.checkIn.localeCompare(b.checkIn));
}

export function isUnavailable(date: string, ranges: UnavailableRange[]): boolean {
  return ranges.some(range => date >= range.checkIn && date < range.checkOut);
}

export function finlandToday(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Helsinki', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}
